import { useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { toast } from 'sonner'
import { Shield, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useFbSettings, useUpdateFbSettings } from '@/api/hooks/useExtension'

export function ConfigTab() {
  const { t } = useLanguage()
  const { data, isLoading } = useFbSettings()
  const updateSettings = useUpdateFbSettings()

  const [minDelay, setMinDelay] = useState<number>(45)
  const [maxDelay, setMaxDelay] = useState<number>(120)
  const [maxPostsPerDay, setMaxPostsPerDay] = useState<number>(50)
  const [batchSize, setBatchSize] = useState<number>(10)
  const [batchPauseMin, setBatchPauseMin] = useState<number>(10)
  const [batchPauseMax, setBatchPauseMax] = useState<number>(20)
  const [appendUniqueId, setAppendUniqueId] = useState(true)
  const [humanizeScroll, setHumanizeScroll] = useState(true)
  const [soundNotification, setSoundNotification] = useState(true)
  const [loaded, setLoaded] = useState(false)

  if (data?.settings && !loaded) {
    setMinDelay(data.settings.minDelay)
    setMaxDelay(data.settings.maxDelay)
    setMaxPostsPerDay(data.settings.maxPostsPerDay)
    setBatchSize(data.settings.batchSize)
    setBatchPauseMin(data.settings.batchPauseMin)
    setBatchPauseMax(data.settings.batchPauseMax)
    setAppendUniqueId(data.settings.appendUniqueId)
    setHumanizeScroll(data.settings.humanizeScroll)
    setSoundNotification(data.settings.soundNotification)
    setLoaded(true)
  }

  const handleSave = () => {
    updateSettings.mutate(
      {
        min_delay: minDelay, max_delay: maxDelay, max_posts_per_day: maxPostsPerDay,
        batch_size: batchSize, batch_pause_min: batchPauseMin, batch_pause_max: batchPauseMax,
        append_unique_id: appendUniqueId, humanize_scroll: humanizeScroll, sound_notification: soundNotification,
      },
      { onSuccess: () => toast.success(t('Configuracoes salvas!')), onError: (e) => toast.error(e.message) },
    )
  }

  if (isLoading) return <Skeleton className="h-64 w-full rounded-2xl" />

  return (
    <div className="space-y-4 max-w-lg">
      {/* Anti-Ban */}
      <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15">
            <Shield className="h-3.5 w-3.5 text-emerald-500" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">{t('Protecao Anti-Ban')}</h3>
            <p className="text-[10px] text-muted-foreground">{t('Configuracoes para evitar restricoes do Facebook.')}</p>
          </div>
        </div>

        <div>
          <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Pausa automatica apos lote')}</Label>
          <p className="text-[10px] text-muted-foreground mb-2">{t('A cada X posts, pausa longa para simular comportamento humano.')}</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">{t('Posts/lote')}</Label>
              <Input type="number" min={3} max={30} value={batchSize} onChange={e => setBatchSize(Number(e.target.value))} className="rounded-lg border-border/40 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">{t('Pausa min')}</Label>
              <Input type="number" min={5} max={60} value={batchPauseMin} onChange={e => setBatchPauseMin(Number(e.target.value))} className="rounded-lg border-border/40 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">{t('Pausa max')}</Label>
              <Input type="number" min={5} max={120} value={batchPauseMax} onChange={e => setBatchPauseMax(Number(e.target.value))} className="rounded-lg border-border/40 h-8 text-xs" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between py-1">
          <div>
            <Label className="text-xs font-medium">{t('ID unico invisivel')}</Label>
            <p className="text-[10px] text-muted-foreground">{t('Caracteres invisiveis para o Facebook tratar cada post como unico')}</p>
          </div>
          <button onClick={() => setAppendUniqueId(!appendUniqueId)} className={`w-10 h-5 rounded-full transition-colors duration-200 ${appendUniqueId ? 'bg-green-500' : 'bg-zinc-600'}`}>
            <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${appendUniqueId ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <div className="flex items-center justify-between py-1">
          <div>
            <Label className="text-xs font-medium">{t('Scroll humano')}</Label>
            <p className="text-[10px] text-muted-foreground">{t('Simula rolagem e movimentos de mouse antes de postar')}</p>
          </div>
          <button onClick={() => setHumanizeScroll(!humanizeScroll)} className={`w-10 h-5 rounded-full transition-colors duration-200 ${humanizeScroll ? 'bg-green-500' : 'bg-zinc-600'}`}>
            <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${humanizeScroll ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {/* Delays */}
      <div className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
            <Clock className="h-3.5 w-3.5 text-primary" />
          </div>
          <h3 className="text-sm font-semibold">{t('Delays entre Posts')}</h3>
        </div>
        <p className="text-[10px] text-muted-foreground">{t('Tempo aleatorio entre cada postagem. Valores maiores = mais seguro.')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] text-muted-foreground">{t('Minimo (s)')}</Label>
            <Input type="number" min={10} max={300} value={minDelay} onChange={e => setMinDelay(Number(e.target.value))} className="rounded-lg border-border/40 h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">{t('Maximo (s)')}</Label>
            <Input type="number" min={15} max={600} value={maxDelay} onChange={e => setMaxDelay(Number(e.target.value))} className="rounded-lg border-border/40 h-8 text-xs" />
          </div>
        </div>

        <div>
          <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Limites')}</Label>
          <div className="mt-1.5">
            <Label className="text-[10px] text-muted-foreground">{t('Max posts por dia')}</Label>
            <Input type="number" min={5} max={200} value={maxPostsPerDay} onChange={e => setMaxPostsPerDay(Number(e.target.value))} className="rounded-lg border-border/40 h-8 text-xs" />
            <p className="text-[10px] text-muted-foreground mt-1">{t('Recomendado: 50 para contas maduras, 20 para contas novas')}</p>
          </div>
        </div>

        <div className="flex items-center justify-between py-1">
          <div>
            <Label className="text-xs font-medium">{t('Notificacao sonora')}</Label>
            <p className="text-[10px] text-muted-foreground">{t('Tocar som ao concluir')}</p>
          </div>
          <button onClick={() => setSoundNotification(!soundNotification)} className={`w-10 h-5 rounded-full transition-colors duration-200 ${soundNotification ? 'bg-green-500' : 'bg-zinc-600'}`}>
            <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${soundNotification ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <Button onClick={handleSave} disabled={updateSettings.isPending} className="w-full rounded-xl">
          {updateSettings.isPending ? t('Salvando...') : t('Salvar Configuracoes')}
        </Button>
      </div>
    </div>
  )
}
